import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
  CircularProgress,
  Avatar,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import {
  NotificationsActive,
  MarkEmailRead,
  Inventory,
  LocalShipping,
  Warning,
  Info,
  CheckCircle,
  Error
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success' | 'inventory' | 'shipping';
  createdAt: string;
  isRead: boolean;
  relatedEntityType?: string;
  relatedEntityId?: string;
  priority: 'low' | 'medium' | 'high';
}

interface NotificationsProps {
  onNotificationClick?: (notification: Notification) => void;
  onMarkAsRead?: (id: string) => Promise<void>;
  fetchNotifications?: () => Promise<Notification[]>;
  maxItems?: number;
}

const NotificationsBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    fontWeight: 600,
    fontSize: '0.75rem',
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: theme.shadows[2]
  }
}));

const NotificationItem = styled(ListItem)(({ theme }) => ({
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    borderColor: alpha(theme.palette.primary.main, 0.2),
    transform: 'translateY(-1px)',
    boxShadow: theme.shadows[4]
  }
}));

const getNotificationIcon = (type: Notification['type']) => {
  switch (type) {
    case 'inventory': return <Inventory />;
    case 'shipping': return <LocalShipping />;
    case 'warning': return <Warning />;
    case 'error': return <Error />;
    case 'success': return <CheckCircle />;
    default: return <Info />;
  }
};

const getTypeColor = (type: Notification['type'], theme: any) => {
  switch (type) {
    case 'inventory': return theme.palette.info.main;
    case 'shipping': return theme.palette.secondary.main;
    case 'warning': return theme.palette.warning.main;
    case 'error': return theme.palette.error.main;
    case 'success': return theme.palette.success.main;
    default: return theme.palette.primary.main;
  }
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

export default function HeaderNotifications({
  onNotificationClick,
  onMarkAsRead,
  fetchNotifications,
  maxItems = 10
}: NotificationsProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const [isOpen, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [marking, setMarking] = useState<string | null>(null);

  const loadNotifications = useCallback(async () => {
    if (!fetchNotifications) return;
    
    setLoading(true);
    try {
      const data = await fetchNotifications();
      const sortedData = data
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
        .slice(0, maxItems);
      setNotifications(sortedData);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
    }
    setLoading(false);
  }, [fetchNotifications, maxItems]);

  useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, loadNotifications]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleMarkAsRead = async (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!onMarkAsRead) return;

    setMarking(id);
    try {
      await onMarkAsRead(id);
      await loadNotifications();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
    setMarking(null);
  };

  const handleNotificationClick = (notification: Notification) => {
    if (onNotificationClick) {
      onNotificationClick(notification);
    }
    setOpen(false);
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          ref={ref}
          onClick={handleOpen}
          sx={{
            color: 'text.primary',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1)
            }
          }}
        >
          <NotificationsBadge
            badgeContent={unreadCount}
            max={99}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <NotificationsActive />
          </NotificationsBadge>
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            width: 400,
            maxWidth: '90vw',
            borderRadius: 2,
            boxShadow: theme.shadows[8],
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Warehouse Alerts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {unreadCount} unread notifications
          </Typography>
        </Box>
        
        <Divider />
        
        <Box sx={{ maxHeight: 400, overflowY: 'auto', p: 1 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress size={32} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight={120}
              p={2}
            >
              <NotificationsActive sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                All caught up!
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                No new warehouse notifications at this time.
              </Typography>
            </Box>
          ) : (
            <List disablePadding sx={{ p: 1 }}>
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    opacity: notification.isRead ? 0.7 : 1,
                    borderLeftWidth: 4,
                    borderLeftStyle: 'solid',
                    borderLeftColor: getTypeColor(notification.type, theme)
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: getTypeColor(notification.type, theme),
                      width: 40,
                      height: 40,
                      mr: 2
                    }}
                  >
                    {getNotificationIcon(notification.type)}
                  </Avatar>
                  
                  <Box flex={1} minWidth={0}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ flexGrow: 1 }}
                        noWrap
                      >
                        {notification.title}
                      </Typography>
                      {notification.priority === 'high' && (
                        <Chip
                          label="High"
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                    
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 0.5,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {notification.message}
                    </Typography>
                    
                    <Typography variant="caption" color="text.disabled" fontWeight={500}>
                      {formatTimeAgo(notification.createdAt)}
                    </Typography>
                  </Box>
                  
                  {!notification.isRead && (
                    <Tooltip title="Mark as read">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMarkAsRead(notification.id, e)}
                        disabled={marking === notification.id}
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          ml: 1,
                          '&:hover': { bgcolor: 'primary.dark' },
                          '&:disabled': { bgcolor: 'action.disabled' }
                        }}
                      >
                        {marking === notification.id ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <MarkEmailRead sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </NotificationItem>
              ))}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}